import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, requestOtp, verifyOtp } from "../lib/api";
import { saveToken } from "../lib/auth";

/** Passwordless phone sign-in for the portal.
 *
 * Two steps:
 *   1. Enter a phone number → request a one-time code (sent over
 *      WhatsApp once delivery is provisioned).
 *   2. Enter the code → exchange it for a JWT and land signed in.
 *
 * This is also how a phone-invited teammate onboards: the same verify
 * call creates their account on first success, so we collect an
 * optional name on the code step for brand-new (invited) users. The
 * backend ignores the name fields when the number already belongs to
 * someone.
 *
 * On success we navigate to "/", where Home redirects each role to its
 * own console (state/district heads → /sh, /dh) — identical to the
 * password path, so both sign-in methods land in the same place. */
export function PhoneLoginForm({ dest }: { dest: string }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Populated only in dev (before WhatsApp is configured): the backend
  // echoes the code so the flow is testable end-to-end.
  const [devCode, setDevCode] = useState<string | null>(null);

  const askForCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || loading) return;
    setError(null);
    setInfo(null);
    setDevCode(null);
    setLoading(true);
    try {
      const resp = await requestOtp(phone.trim());
      setStep("code");
      setInfo("We sent you a code. Enter it below to sign in.");
      if (resp.dev_code) setDevCode(resp.dev_code);
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setError(null);
    setLoading(true);
    try {
      const resp = await verifyOtp({
        phone: phone.trim(),
        code: code.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      saveToken(resp.token);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const resetToPhone = () => {
    setStep("phone");
    setCode("");
    setError(null);
    setInfo(null);
    setDevCode(null);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">
          {error}
        </div>
      )}
      {info && !error && (
        <div className="bg-green-50 text-green-800 text-sm p-2 rounded">
          {info}
        </div>
      )}

      {step === "phone" ? (
        <form onSubmit={askForCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Phone number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              autoFocus
              placeholder="+91 98765 43210"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-agrilens-green"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use the number your admin invited, or your registered
              number. We'll send a one-time code.
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-agrilens-green text-white font-medium py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send code"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Code sent to {phone}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              placeholder="6-digit code"
              className="w-full border rounded px-3 py-2 tracking-widest focus:outline-none focus:ring-2 focus:ring-agrilens-green"
            />
            {devCode && (
              <p className="text-xs text-amber-700 mt-1">
                Dev mode — your code is <b>{devCode}</b>.
              </p>
            )}
          </div>

          {/* First-time (invited) accounts: optional name so the
              profile isn't blank. Ignored for existing users. */}
          <details className="text-sm">
            <summary className="cursor-pointer text-agrilens-green">
              First time here? Add your name
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-agrilens-green"
              />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-agrilens-green"
              />
            </div>
          </details>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-agrilens-green text-white font-medium py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Verify & sign in"}
          </button>
          <button
            type="button"
            onClick={resetToPhone}
            className="w-full text-xs text-agrilens-green hover:underline"
          >
            Use a different number
          </button>
        </form>
      )}
    </div>
  );
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}
